import React from 'react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  if (totalPages <= 1) return null

  // Build the page numbers to show (at most 7: always show first/last, with ellipsis)
  const pages: (number | '...')[] = []
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
  } else {
    pages.push(1)
    if (currentPage > 3) pages.push('...')
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push('...')
    pages.push(totalPages)
  }

  return (
    <>
      <style>{`
        .pagination {
          display: flex;
          align-items: center;
          gap: 4px;
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.82rem;
          justify-content: center;
          padding: 16px 0;
          flex-wrap: wrap;
        }
        .pagination-btn {
          background: none;
          border: 1px solid var(--border-color);
          color: var(--text-secondary);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.82rem;
          padding: 5px 10px;
          border-radius: 3px;
          cursor: pointer;
          transition: all 0.15s ease;
          min-width: 36px;
          text-align: center;
        }
        .pagination-btn:hover:not(:disabled) {
          border-color: var(--accent-green);
          color: var(--accent-green);
        }
        .pagination-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .pagination-btn.active {
          background-color: var(--accent-green);
          border-color: var(--accent-green);
          color: #000;
          font-weight: 700;
        }
        .pagination-btn:focus-visible {
          outline: 2px solid var(--accent-cyan);
          outline-offset: 2px;
        }
        .pagination-ellipsis {
          color: var(--text-muted, #555);
          padding: 5px 4px;
          user-select: none;
        }
      `}</style>
      <nav className="pagination" aria-label="Pagination">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Previous page"
        >
          [&lt; prev]
        </button>

        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="pagination-ellipsis">
              ...
            </span>
          ) : (
            <button
              key={p}
              className={`pagination-btn${p === currentPage ? ' active' : ''}`}
              onClick={() => onPageChange(p as number)}
              aria-label={`Page ${p}`}
              aria-current={p === currentPage ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}

        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
        >
          [next &gt;]
        </button>
      </nav>
    </>
  )
}
